import { useEffect, useState, type FormEvent } from 'react';
import Icon from '../components/Icon';
import PageHeader from '../components/PageHeader';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { ApiError } from '../lib/api';
import {
  EXECUTION_ACTION_FILTERS,
  EXECUTION_STATUS_FILTERS,
  getExecutionRequest,
  listExecutionsRequest,
  type ExecutionActionFilter,
  type ExecutionHistoryItem,
  type ExecutionStatusFilter,
} from '../lib/automation-executions-api';

type ListStatus = 'loading' | 'ready' | 'error';
type DetailStatus = 'loading' | 'ready' | 'error' | 'not_found';

interface Filters {
  automationId: string;
  status: string;
  actionType: string;
  externalEventId: string;
}

const EMPTY_FILTERS: Filters = {
  automationId: '',
  status: '',
  actionType: '',
  externalEventId: '',
};

const PAGE_SIZE = 25;
const MAX_FILTER_LENGTH = 256;

/** Persian labels for the backend status vocabulary; unknown values fall back raw. */
const STATUS_LABELS: Record<string, string> = {
  pending: 'در انتظار',
  pending_retry: 'در انتظار تلاش مجدد',
  success: 'موفق',
  failed: 'ناموفق',
  blocked: 'مسدود',
};

const STATUS_BADGE_CLASSES: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-600',
  pending_retry: 'bg-amber-50 text-amber-700',
  success: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-rose-50 text-rose-700',
  blocked: 'bg-rose-100 text-rose-800',
};

/** Persian labels for every schema action_type; unknown values fall back raw. */
const ACTION_LABELS: Record<string, string> = {
  reply_comment: 'پاسخ کامنت',
  private_reply: 'پاسخ خصوصی',
  send_dm_text: 'پیام متنی',
  send_dm_quick_replies: 'دکمه پاسخ سریع',
  send_dm_template: 'قالب پیام',
  advance_flow: 'گام بعدی جریان',
};

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function statusBadgeClasses(status: string): string {
  return STATUS_BADGE_CLASSES[status] ?? 'bg-slate-100 text-slate-600';
}

function actionLabel(actionType: string | null): string {
  if (actionType === null) {
    return '—';
  }
  return ACTION_LABELS[actionType] ?? actionType;
}

function formatTimestamp(timestamp: number | null): string {
  if (timestamp === null) {
    return '—';
  }
  return new Date(timestamp).toLocaleString('fa-IR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Generic Persian error mapping — raw server messages (which may contain
 * internal details) are never shown to the user.
 */
function listErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return 'برای ادامه دوباره وارد حساب خود شوید.';
    }
    if (error.status >= 500) {
      return 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.';
    }
  }
  return 'دریافت گزارش رویدادها ناموفق بود. دوباره تلاش کنید.';
}

function detailErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return 'برای ادامه دوباره وارد حساب خود شوید.';
    }
    if (error.status >= 500) {
      return 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.';
    }
  }
  return 'دریافت جزئیات رویداد ناموفق بود. دوباره تلاش کنید.';
}

function hasAnyFilter(filters: Filters): boolean {
  return (
    filters.automationId !== '' ||
    filters.status !== '' ||
    filters.actionType !== '' ||
    filters.externalEventId !== ''
  );
}

interface DetailRowProps {
  label: string;
  value: string;
  ltr?: boolean;
}

function DetailRow({ label, value, ltr = false }: DetailRowProps) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-slate-100 pb-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="shrink-0 text-xs font-semibold text-slate-500">{label}</dt>
      <dd
        dir={ltr ? 'ltr' : undefined}
        className={`min-w-0 break-all text-sm text-slate-800 ${ltr ? 'text-left' : 'text-right'}`}
      >
        {value}
      </dd>
    </div>
  );
}

export default function ExecutionLogsPage() {
  useDocumentTitle('گزارش رویدادها');

  const [listStatus, setListStatus] = useState<ListStatus>('loading');
  const [executions, setExecutions] = useState<ExecutionHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [listError, setListError] = useState<string | null>(null);
  const [listReloadKey, setListReloadKey] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);
  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailStatus, setDetailStatus] = useState<DetailStatus>('loading');
  const [detail, setDetail] = useState<ExecutionHistoryItem | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailReloadKey, setDetailReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listExecutionsRequest({
      limit: PAGE_SIZE,
      offset,
      automationId: appliedFilters.automationId,
      status: appliedFilters.status,
      actionType: appliedFilters.actionType,
      externalEventId: appliedFilters.externalEventId,
    })
      .then((response) => {
        if (!cancelled) {
          setExecutions(response.executions);
          setTotal(response.total);
          setListError(null);
          setListStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setListError(listErrorMessage(error));
          setListStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [offset, appliedFilters, listReloadKey]);

  useEffect(() => {
    if (selectedId === null) {
      return;
    }
    let cancelled = false;
    getExecutionRequest(selectedId)
      .then((response) => {
        if (!cancelled) {
          setDetail(response.execution);
          setDetailError(null);
          setDetailStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          if (error instanceof ApiError && error.status === 404) {
            setDetail(null);
            setDetailError(null);
            setDetailStatus('not_found');
          } else {
            setDetail(null);
            setDetailError(detailErrorMessage(error));
            setDetailStatus('error');
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, detailReloadKey]);

  function handleRetryList(): void {
    setListError(null);
    setListStatus('loading');
    setListReloadKey((key) => key + 1);
  }

  function handleApplyFilters(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setAppliedFilters({
      automationId: draft.automationId.trim(),
      status: draft.status,
      actionType: draft.actionType,
      externalEventId: draft.externalEventId.trim(),
    });
    setOffset(0);
    setListError(null);
    setListStatus('loading');
  }

  function handleClearFilters(): void {
    setDraft(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setOffset(0);
    setListError(null);
    setListStatus('loading');
  }

  function handlePrevPage(): void {
    if (offset === 0 || listStatus !== 'ready') {
      return;
    }
    setListStatus('loading');
    setOffset(Math.max(0, offset - PAGE_SIZE));
  }

  function handleNextPage(): void {
    if (offset + PAGE_SIZE >= total || listStatus !== 'ready') {
      return;
    }
    setListStatus('loading');
    setOffset(offset + PAGE_SIZE);
  }

  function openDetail(id: string): void {
    setSelectedId(id);
    setDetail(null);
    setDetailError(null);
    setDetailStatus('loading');
    setDetailReloadKey((key) => key + 1);
  }

  function closeDetail(): void {
    setSelectedId(null);
    setDetail(null);
    setDetailError(null);
    setDetailReloadKey((key) => key + 1);
  }

  function handleRetryDetail(): void {
    setDetailError(null);
    setDetailStatus('loading');
    setDetailReloadKey((key) => key + 1);
  }

  const activeFilters = hasAnyFilter(appliedFilters);
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const prevDisabled = offset === 0 || listStatus !== 'ready';
  const nextDisabled = offset + PAGE_SIZE >= total || listStatus !== 'ready';

  return (
    <div className="space-y-6">
      <PageHeader
        title="گزارش رویدادها"
        description="تاریخچه فقط‌خواندنی اجرای اتومیشن‌ها: وضعیت، کد پاسخ و تلاش‌ها"
      />

      <form
        data-testid="execution-filters"
        onSubmit={(event) => handleApplyFilters(event)}
        noValidate
        className="rounded-xl border border-slate-200 bg-white p-4"
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label
              htmlFor="filter-automation-id"
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              شناسه اتوماسیون
            </label>
            <input
              id="filter-automation-id"
              type="text"
              dir="ltr"
              maxLength={MAX_FILTER_LENGTH}
              value={draft.automationId}
              onChange={(event) =>
                setDraft((current) => ({ ...current, automationId: event.target.value }))
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-left text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              placeholder="auto-123"
            />
          </div>
          <div>
            <label
              htmlFor="filter-status"
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              وضعیت
            </label>
            <select
              id="filter-status"
              value={draft.status}
              onChange={(event) =>
                setDraft((current) => ({ ...current, status: event.target.value }))
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            >
              <option value="">همه وضعیت‌ها</option>
              {EXECUTION_STATUS_FILTERS.map((value: ExecutionStatusFilter) => (
                <option key={value} value={value}>
                  {statusLabel(value)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="filter-action-type"
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              نوع اکشن
            </label>
            <select
              id="filter-action-type"
              value={draft.actionType}
              onChange={(event) =>
                setDraft((current) => ({ ...current, actionType: event.target.value }))
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            >
              <option value="">همه اکشن‌ها</option>
              {EXECUTION_ACTION_FILTERS.map((value: ExecutionActionFilter) => (
                <option key={value} value={value}>
                  {actionLabel(value)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="filter-external-event-id"
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              شناسه رویداد
            </label>
            <input
              id="filter-external-event-id"
              type="text"
              dir="ltr"
              maxLength={MAX_FILTER_LENGTH}
              value={draft.externalEventId}
              onChange={(event) =>
                setDraft((current) => ({ ...current, externalEventId: event.target.value }))
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-left text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              placeholder="evt-1"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="submit"
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-800"
          >
            اعمال فیلترها
          </button>
          <button
            type="button"
            onClick={handleClearFilters}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            پاک کردن فیلترها
          </button>
          {activeFilters && (
            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
              فیلتر فعال
            </span>
          )}
        </div>
      </form>

      {listStatus === 'loading' && (
        <div
          role="status"
          data-testid="list-loading"
          className="rounded-xl border border-slate-200 bg-white p-10 text-center"
        >
          <span className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
          <p className="mt-3 text-sm text-slate-500">در حال دریافت رویدادها…</p>
        </div>
      )}

      {listStatus === 'error' && (
        <div className="rounded-xl border border-rose-200 bg-white p-6 text-center">
          <p role="alert" data-testid="list-error" className="text-sm text-rose-700">
            {listError}
          </p>
          <button
            type="button"
            onClick={handleRetryList}
            className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            تلاش مجدد
          </button>
        </div>
      )}

      {listStatus === 'ready' && executions.length === 0 && !activeFilters && (
        <div
          data-testid="empty-state"
          className="rounded-xl border border-slate-200 bg-white p-10 text-center"
        >
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-400">
            <Icon name="list" className="h-6 w-6" />
          </span>
          <h3 className="mt-4 text-base font-bold text-slate-800">هنوز رویدادی ثبت نشده است</h3>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">
            پس از اجرای اتومیشن‌ها، نتیجه هر اجرا با وضعیت و کد پاسخ در اینجا ثبت می‌شود.
          </p>
        </div>
      )}

      {listStatus === 'ready' && executions.length === 0 && activeFilters && (
        <div
          data-testid="filtered-empty-state"
          className="rounded-xl border border-slate-200 bg-white p-10 text-center"
        >
          <h3 className="text-base font-bold text-slate-800">برای این فیلترها نتیجه‌ای یافت نشد</h3>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">
            فیلترهای فعلی را تغییر دهید یا آن‌ها را پاک کنید تا همه رویدادها نمایش داده شوند.
          </p>
          <button
            type="button"
            onClick={handleClearFilters}
            className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            نمایش همه رویدادها
          </button>
        </div>
      )}

      {listStatus === 'ready' && executions.length > 0 && (
        <ul data-testid="execution-list" className="space-y-3">
          {executions.map((row) => (
            <li key={row.id} data-testid="execution-row" data-execution-id={row.id}>
              <button
                type="button"
                onClick={() => openDetail(row.id)}
                aria-label={`جزئیات رویداد ${row.external_event_id}`}
                className="flex w-full flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 text-right transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusBadgeClasses(row.status)}`}
                    >
                      {statusLabel(row.status)}
                    </span>
                    <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] text-violet-700">
                      {actionLabel(row.action_type)}
                    </span>
                    <span className="text-xs text-slate-400">
                      {formatTimestamp(row.created_at)}
                    </span>
                  </div>
                  <p dir="ltr" className="break-all text-left text-xs text-slate-500">
                    اتوماسیون: {row.automation_id}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span>تلاش: {row.attempt}</span>
                    {row.http_status !== null && (
                      <span dir="ltr" className="text-left">
                        HTTP {row.http_status}
                      </span>
                    )}
                    {row.error_code !== null && (
                      <span dir="ltr" className="break-all text-left text-rose-600">
                        {row.error_code}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  aria-hidden="true"
                  className="shrink-0 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"
                >
                  جزئیات
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {listStatus === 'ready' && total > 0 && (
        <div
          data-testid="pagination"
          className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <p className="text-sm text-slate-500">
            مجموع:{' '}
            <span data-testid="total-count" className="font-semibold text-slate-700">
              {total}
            </span>{' '}
            رویداد — صفحه {page} از {pageCount}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={prevDisabled}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              قبلی
            </button>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={nextDisabled}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              بعدی
            </button>
          </div>
        </div>
      )}

      {selectedId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="execution-detail-title"
            data-testid="execution-detail-dialog"
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-200 bg-white p-6 shadow-xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 id="execution-detail-title" className="text-base font-bold text-slate-900">
                جزئیات رویداد
              </h3>
              <button
                type="button"
                onClick={closeDetail}
                aria-label="بستن"
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </div>

            {detailStatus === 'loading' && (
              <div data-testid="detail-loading" className="py-8 text-center">
                <span className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
                <p className="mt-3 text-sm text-slate-500">در حال بارگذاری جزئیات…</p>
              </div>
            )}

            {detailStatus === 'not_found' && (
              <div data-testid="detail-not-found" className="py-6 text-center">
                <p className="text-sm font-semibold text-rose-700">
                  این رویداد یافت نشد یا در دسترس نیست.
                </p>
                <p className="mt-2 text-xs leading-6 text-slate-500">
                  در صورت حذف اتوماسیون مرتبط، تاریخچه آن نمایش داده نمی‌شود. این پیام برای
                  رویدادهای موجود و حذف‌شده یکسان است.
                </p>
              </div>
            )}

            {detailStatus === 'error' && (
              <div className="py-6 text-center">
                <p role="alert" data-testid="detail-error" className="text-sm text-rose-700">
                  {detailError}
                </p>
                <button
                  type="button"
                  onClick={handleRetryDetail}
                  className="mt-4 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  تلاش مجدد
                </button>
              </div>
            )}

            {detailStatus === 'ready' && detail !== null && (
              <dl className="space-y-3">
                <DetailRow label="زمان اجرا" value={formatTimestamp(detail.created_at)} />
                <div className="flex flex-col gap-1 border-b border-slate-100 pb-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                  <dt className="shrink-0 text-xs font-semibold text-slate-500">وضعیت</dt>
                  <dd>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadgeClasses(detail.status)}`}
                    >
                      {statusLabel(detail.status)}
                    </span>
                  </dd>
                </div>
                <DetailRow label="نوع اکشن" value={actionLabel(detail.action_type)} />
                <DetailRow label="اتوماسیون" value={detail.automation_id} ltr />
                <DetailRow label="شناسه رویداد" value={detail.external_event_id} ltr />
                <DetailRow label="تعداد تلاش" value={String(detail.attempt)} ltr />
                {detail.http_status !== null && (
                  <DetailRow label="کد HTTP" value={String(detail.http_status)} ltr />
                )}
                {detail.error_code !== null && (
                  <DetailRow label="کد خطا" value={detail.error_code} ltr />
                )}
                {detail.action_id !== null && (
                  <DetailRow label="شناسه اکشن" value={detail.action_id} ltr />
                )}
                {detail.next_attempt_at !== null && (
                  <DetailRow
                    label="زمان تلاش بعدی"
                    value={formatTimestamp(detail.next_attempt_at)}
                  />
                )}
                <DetailRow label="آخرین به‌روزرسانی" value={formatTimestamp(detail.updated_at)} />
              </dl>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
